import { ChangeEvent, useCallback, useState } from 'react';
import { Card, Input, Label, Select, TextArea } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFormContext } from 'react-hook-form';
import { DE_LOCALE } from 'utils/i18n';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { PURPOSEOPTIONS } from '../helpers/purposeOfFundsHelper';
import { TSignupFormDataDE } from '../NewSignupFormDE/NewSignupFormDE.types';
import signupFormStyles from '../SignupForm/SignupForm.module.css';
import styles from './LoanDetailsDE.module.css';

export const LoanDetailsDE = () => {
  const { splitTests } = useIntroducerContext();
  const [additionalInformationRequired, setAdditionalInformationRequired] =
    useState(false);
  const [showLoanPurposeDetails, setShowLoanPurposeDetails] = useState(true);
  const { trackEvent } = useAmplitude();
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<TSignupFormDataDE>();

  const handleProductTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) {
      return;
    }

    trackEvent(AMPLITUDE_EVENTS.productTypeSelected, {
      'Loan Duration': e.target.value,
    });
  };

  const formatCurrency = useCallback(() => {
    const amount = watch('amount')?.replace(/[^0-9]+/g, '');
    if (!amount) {
      return '';
    }
    return parseInt(amount).toLocaleString(DE_LOCALE);
  }, [watch]);

  const handleLoanPurposeChange = ({
    target: { value },
  }: ChangeEvent<HTMLSelectElement>) => {
    setAdditionalInformationRequired(value === 'other');
    setShowLoanPurposeDetails(value !== 'personal_loan');

    trackEvent(AMPLITUDE_EVENTS.loanPurposeSelected, {
      'Loan purpose': value,
    });
  };

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>
        <Trans>Loan requirements</Trans>
      </h2>
      <div className={signupFormStyles.cardContents}>
        <div className={signupFormStyles.formField}>
          <Input
            {...register('amount')}
            value={formatCurrency()}
            className={styles.loanAmount}
            label={t`Amount`}
            iconRight="euro"
            type="text"
            min={0}
            error={errors.amount?.message}
          />
        </div>
        <div className={signupFormStyles.formField}>
          <Select
            {...register('loanDuration')}
            onChange={handleProductTypeChange}
            label={t`Term length`}
            placeholder={t`Choose an option`}
            error={errors.loanDuration && errors.loanDuration.message}
          >
            <option value="12-months">
              <Trans>Short term: 1 - 12 months</Trans>
            </option>
            <option value="24-months">
              <Trans>Medium term: 13 - 24 months</Trans>
            </option>
            {splitTests?.hasAccessToPrimeLoans && (
              <option value="60-months">
                <Trans>Longer term: 36 - 60 months</Trans>
              </option>
            )}
          </Select>
        </div>
        <div className={signupFormStyles.formField}>
          <Select
            {...register('loanPurpose')}
            label={t`Loan purpose`}
            onChange={handleLoanPurposeChange}
            error={errors.loanPurpose && errors.loanPurpose.message}
            placeholder={t`Choose an option`}
          >
            {PURPOSEOPTIONS()
              .filter((option) => option.enabledForDE)
              .map((option) => {
                return (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                );
              })}
          </Select>
        </div>
        {showLoanPurposeDetails && (
          <div className={signupFormStyles.formField}>
            <Label className={styles.label} htmlFor="loanPurposeDetails">
              <Trans>Additional information</Trans>
              {!additionalInformationRequired && (
                <span
                  data-testid="loan-purpose-optional"
                  className={styles.optional}
                >
                  (<Trans>optional</Trans>)
                </span>
              )}
            </Label>
            <TextArea
              {...register('loanPurposeDetails')}
              data-testid="loan-purpose-details"
              className={styles.textArea}
              id="loanPurposeDetails"
              name="loanPurposeDetails"
              error={
                errors.loanPurposeDetails && errors.loanPurposeDetails.message
              }
            />
          </div>
        )}
      </div>
    </Card>
  );
};
